import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { 
  Sparkles,
  Trophy,
  BarChart3,
  Database,
  Users,
  Calendar,
  Activity,
  Target,
  TrendingUp,
  Shield,
  Zap,
  Globe,
  Smartphone,
  Clock,
  Search,
  Bell,
  Settings,
  ChevronRight,
  Check,
  Star,
  Lock,
  Unlock
} from 'lucide-react';
import { useSite } from '../../contexts/SiteContext';

const FeaturesPage: React.FC = () => {
  const site = useSite();
  const [selectedCategory, setSelectedCategory] = useState('all');

  const features = [
    {
      category: 'analytics',
      title: 'Advanced Analytics',
      description: 'Deep dive into performance metrics with AI-powered insights',
      icon: BarChart3,
      color: 'from-blue-500 to-blue-600',
      capabilities: [
        'Real-time performance tracking',
        'Predictive modeling',
        'Custom metric creation',
        'Comparative analysis'
      ],
      status: 'available'
    },
    {
      category: 'data',
      title: 'Comprehensive Data Coverage',
      description: 'Access to historical and real-time data across all major leagues',
      icon: Database,
      color: 'from-green-500 to-green-600',
      capabilities: [
        'Complete championship history',
        'Player & team statistics',
        'Live game updates',
        'Season-by-season records'
      ],
      status: 'available'
    },
    {
      category: 'tracking',
      title: 'Live Score Tracking',
      description: 'Real-time scores and game updates with instant notifications',
      icon: Activity,
      color: 'from-purple-500 to-purple-600',
      capabilities: [
        'Live game scores',
        'Play-by-play updates',
        'Push notifications',
        'Multi-game tracking'
      ],
      status: 'available'
    },
    {
      category: 'teams',
      title: 'Team Management',
      description: 'Comprehensive team profiles with rosters and performance data',
      icon: Users,
      color: 'from-orange-500 to-orange-600',
      capabilities: [
        'Complete team rosters',
        'Historical performance',
        'Head-to-head comparisons',
        'Franchise records'
      ],
      status: 'available'
    },
    {
      category: 'predictions',
      title: 'AI Predictions',
      description: 'Machine learning models for game outcome predictions',
      icon: TrendingUp,
      color: 'from-pink-500 to-pink-600',
      capabilities: [
        'Game outcome predictions',
        'Player performance forecasts',
        'Season projections',
        'Playoff probability'
      ],
      status: 'coming-soon'
    },
    {
      category: 'mobile',
      title: 'Mobile Experience',
      description: 'Fully responsive design optimized for all devices',
      icon: Smartphone,
      color: 'from-indigo-500 to-indigo-600',
      capabilities: [
        'Native mobile app',
        'Offline data access',
        'Cross-platform sync',
        'Touch-optimized interface'
      ],
      status: 'coming-soon'
    }
  ];

  const categories = [
    { id: 'all', name: 'All Features', count: features.length },
    { id: 'analytics', name: 'Analytics', count: features.filter(f => f.category === 'analytics').length },
    { id: 'data', name: 'Data', count: features.filter(f => f.category === 'data').length },
    { id: 'tracking', name: 'Tracking', count: features.filter(f => f.category === 'tracking').length },
    { id: 'teams', name: 'Teams', count: features.filter(f => f.category === 'teams').length }
  ];

  const filteredFeatures = selectedCategory === 'all' 
    ? features 
    : features.filter(f => f.category === selectedCategory);

  const plans = [
    {
      name: 'Free',
      price: '$0',
      period: 'forever',
      description: 'Essential features for casual fans',
      features: [
        'Basic statistics',
        'Championship history',
        'Team profiles',
        'Live scores (delayed)',
        'Limited API calls'
      ],
      cta: 'Get Started',
      popular: false
    },
    {
      name: 'Pro',
      price: '$9.99',
      period: 'per month',
      description: 'Advanced features for serious fans',
      features: [
        'Everything in Free',
        'Real-time live scores',
        'Advanced analytics',
        'Unlimited API calls',
        'Priority support',
        'Custom alerts'
      ],
      cta: 'Start Free Trial',
      popular: true
    },
    {
      name: 'Enterprise',
      price: 'Custom',
      period: 'contact us',
      description: 'Tailored solutions for organizations',
      features: [
        'Everything in Pro',
        'Custom integrations',
        'Dedicated support',
        'SLA guarantee',
        'White-label options',
        'Advanced API access'
      ],
      cta: 'Contact Sales',
      popular: false
    }
  ];

  return (
    <div className="min-h-screen py-12">
      {/* Page Header */}
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        className="mb-12"
      >
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-8">
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ delay: 0.1 }}
              className="inline-flex items-center space-x-2 px-4 py-1.5 rounded-full border border-primary/30 mb-6"
            >
              <Sparkles className="w-4 h-4 text-primary" />
              <span className="text-primary text-xs font-semibold uppercase tracking-wider">
                Platform Features
              </span>
            </motion.div>
            
            <h1 className="text-4xl md:text-6xl font-black mb-4">
              <span className="text-white">POWERFUL </span>
              <span className="neon-text">FEATURES</span>
            </h1>
            <p className="text-xl text-gray-400 max-w-3xl mx-auto">
              Everything you need to track, analyze, and understand {site.sport ? `${site.sport} ` : 'sports '}performance
            </p>
          </div>

          {/* Category Filter */}
          <div className="flex flex-wrap justify-center gap-3 mb-12">
            {categories.map((category) => (
              <button
                key={category.id}
                onClick={() => setSelectedCategory(category.id)}
                className={`px-4 py-2 rounded-lg border transition-all ${
                  selectedCategory === category.id
                    ? 'bg-primary/20 border-primary text-primary'
                    : 'bg-surface/30 border-border text-gray-400 hover:text-white hover:border-gray-600'
                }`}
              >
                {category.name}
                <span className="ml-2 text-xs opacity-60">({category.count})</span>
              </button>
            ))}
          </div>

          {/* Features Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mb-20">
            {filteredFeatures.map((feature, index) => {
              const Icon = feature.icon;
              return (
                <motion.div
                  key={feature.title}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.1 }}
                  className="glass-card p-6 hover:scale-105 transition-transform"
                >
                  <div className="flex items-start justify-between mb-4">
                    <div className={`p-3 rounded-xl bg-gradient-to-br ${feature.color}`}>
                      <Icon className="w-6 h-6 text-white" />
                    </div>
                    {feature.status === 'coming-soon' && (
                      <span className="badge badge-secondary text-xs">Coming Soon</span>
                    )}
                  </div>
                  
                  <h3 className="text-xl font-bold text-white mb-2">{feature.title}</h3>
                  <p className="text-gray-400 text-sm mb-4">{feature.description}</p>
                  
                  <div className="space-y-2">
                    {feature.capabilities.map((capability) => (
                      <div key={capability} className="flex items-start gap-2">
                        <Check className="w-4 h-4 text-success mt-0.5 flex-shrink-0" />
                        <span className="text-sm text-gray-300">{capability}</span>
                      </div>
                    ))}
                  </div>
                </motion.div>
              );
            })}
          </div>

          {/* Pricing Section */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="mb-20"
          >
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
                Choose Your Plan
              </h2>
              <p className="text-lg text-gray-400">
                Select the perfect plan for your needs
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {plans.map((plan, index) => (
                <motion.div
                  key={plan.name}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.2 + index * 0.1 }}
                  className={`glass-card p-8 relative ${
                    plan.popular ? 'border-2 border-primary' : ''
                  }`}
                >
                  {plan.popular && (
                    <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                      <span className="badge badge-primary px-4 py-1">Most Popular</span>
                    </div>
                  )}
                  
                  <div className="text-center mb-6">
                    <h3 className="text-2xl font-bold text-white mb-2">{plan.name}</h3>
                    <div className="flex items-baseline justify-center gap-1">
                      <span className="text-4xl font-bold text-primary">{plan.price}</span>
                      <span className="text-gray-400 text-sm">/{plan.period}</span>
                    </div>
                    <p className="text-gray-400 text-sm mt-2">{plan.description}</p>
                  </div>
                  
                  <div className="space-y-3 mb-8">
                    {plan.features.map((feature) => (
                      <div key={feature} className="flex items-start gap-3">
                        <Check className="w-5 h-5 text-success flex-shrink-0 mt-0.5" />
                        <span className="text-sm text-gray-300">{feature}</span>
                      </div>
                    ))}
                  </div>
                  
                  <button className={`w-full py-3 rounded-lg font-semibold transition-all ${
                    plan.popular
                      ? 'bg-primary text-white hover:bg-primary-dark'
                      : 'bg-surface/50 text-white border border-border hover:border-primary'
                  }`}>
                    {plan.cta}
                  </button>
                </motion.div>
              ))}
            </div>
          </motion.div>

          {/* CTA Section */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="glass-card p-12 text-center"
          >
            <Trophy className="w-16 h-16 text-primary mx-auto mb-6" />
            <h2 className="text-3xl font-bold text-white mb-4">
              Ready to Get Started?
            </h2>
            <p className="text-lg text-gray-400 max-w-2xl mx-auto mb-8">
              Join thousands of sports fans and analysts using our platform to gain deeper insights into the game.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <button className="px-8 py-3 bg-primary text-white rounded-lg font-semibold hover:bg-primary-dark transition-all">
                Start Free Trial
              </button>
              <button className="px-8 py-3 bg-surface/50 text-white border border-border rounded-lg font-semibold hover:border-primary transition-all">
                View Documentation
              </button>
            </div>
          </motion.div>
        </div>
      </motion.div>
    </div>
  );
};

export default FeaturesPage;